/**
 * indexRestore — put collapsed triangles back from a pristine copy of the index buffer.
 *
 * The seam stitcher's band mask hides a triangle by collapsing it in place (idx[a+1] = idx[a+2] =
 * idx[a]), which is destructive. The restore used to rewrite the triple as 3t, 3t+1, 3t+2 because
 * the chunk index buffer was an identity permutation. It is a real index buffer now (shared
 * vertices, MeshGeometry), so the original triple has to be KEPT: the first mask on a geometry
 * snapshots its index buffer (lazily — only seam-adjacent chunks pay), and every restore copies the
 * triple back from that snapshot. A restore that lands on a re-meshed (fresh) geometry is then a
 * no-op instead of writing an identity permutation over real indices — which drew long slivers
 * across chunks on both the solid and the transparent layer.
 */
import type * as THREE from 'three';

const KEY = 'pristineIndex';

/** The pristine index buffer of `g`, snapshotting it now if this is the first call. */
export function pristineIndices(g: THREE.BufferGeometry): Uint32Array | null {
  const idx = g.index?.array as Uint32Array | undefined;
  if (!idx) return null;
  let orig = g.userData[KEY] as Uint32Array | undefined;
  if (!orig || orig.length !== idx.length) {
    orig = idx.slice();
    g.userData[KEY] = orig;
  }
  return orig;
}

/** Restore triangles `tris` of `g` from its pristine snapshot. Returns false if `g` has no index. */
export function restoreTriangles(g: THREE.BufferGeometry, tris: readonly number[]): boolean {
  const idx = g.index?.array as Uint32Array | undefined;
  if (!idx) return false;
  const orig = pristineIndices(g)!;
  for (const t of tris) {
    const a = t * 3;
    if (a + 2 >= idx.length) continue;
    idx[a] = orig[a]; idx[a + 1] = orig[a + 1]; idx[a + 2] = orig[a + 2];
  }
  g.index!.needsUpdate = true;
  return true;
}
